import {
    ChangeDetectionStrategy,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    forwardRef,
    input,
    InputSignal,
    signal
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'sh-datepicker-accessor',
    templateUrl: './sh-datepicker-accessor.component.html',
    standalone: true,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ShDatepickerAccessorComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ShDatepickerAccessorComponent implements ControlValueAccessor {
    public label: InputSignal<string> = input<string>('');
    public format: InputSignal<string | undefined> = input<string | undefined>(undefined);
    public separator: InputSignal<string | undefined> = input<string | undefined>(undefined);
    public months: InputSignal<number | undefined> = input<number | undefined>(undefined);
    public range: InputSignal<boolean> = input<boolean>(false);
    public futureDateDisable: InputSignal<boolean> = input<boolean>(false);
    public pastDateDisable: InputSignal<boolean> = input<boolean>(false);
    public condensed: InputSignal<boolean> = input<boolean>(false);
    public editable: InputSignal<boolean> = input<boolean>(false);
    public responsive: InputSignal<string | undefined> = input<string | undefined>(undefined);
    public scrollpicker: InputSignal<string | undefined> = input<string | undefined>(undefined);
    public readonly: InputSignal<boolean> = input<boolean>(false);
    public error: InputSignal<boolean> = input<boolean>(false);
    public success: InputSignal<boolean> = input<boolean>(false);
    public safety: InputSignal<boolean> = input<boolean>(false);
    public hint: InputSignal<string | undefined> = input<string | undefined>(undefined);
    public errorMessage: InputSignal<string | undefined> = input<string | undefined>(undefined);
    public noBorder: InputSignal<boolean> = input<boolean>(false);
    public mandatory: InputSignal<boolean> = input<boolean>(false);
    public max: InputSignal<string | undefined> = input<string | undefined>(undefined);
    public min: InputSignal<string | undefined> = input<string | undefined>(undefined);
    public noClear: InputSignal<boolean | undefined> = input<boolean | undefined>(undefined);
    public requireApply: InputSignal<boolean | undefined> = input<boolean | undefined>(undefined);
    public locale: InputSignal<string | undefined> = input<string | undefined>(undefined);
    // eslint-disable-next-line @angular-eslint/no-input-rename
    public disabledInput: InputSignal<boolean> = input<boolean>(false, { alias: 'disabled' });

    protected readonly value = signal('');
    protected disabled = false;

    private onChange: (value: unknown) => void = () => void 0;
    private onTouched: () => void = () => void 0;

    public writeValue(value: unknown): void {
        if (Array.isArray(value) && value.length === 2 && value[0] instanceof Date && value[1] instanceof Date) {
            this.value.set(`${this.toMdy(value[0])}-${this.toMdy(value[1])}`);
        } else if (value instanceof Date) {
            this.value.set(this.toMdy(value));
        } else if (typeof value === 'string' && value) {
            const parsed = new Date(value);
            this.value.set(isNaN(parsed.getTime()) ? value : this.toMdy(parsed));
        } else {
            this.value.set('');
        }
    }

    public registerOnChange(fn: (value: unknown) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    protected handleValueChanged(event: Event): void {
        const target = event.target as { value?: string; standardValue?: Date[] };
        const nextValue = target.value ?? '';

        if (nextValue === this.value()) {
            return;
        }

        this.value.set(nextValue);

        if (this.range() && target.standardValue?.length === 2) {
            this.onChange(target.standardValue);
        } else if (!this.range() && target.standardValue?.length === 1) {
            this.onChange(target.standardValue[0]);
        } else {
            this.onChange(nextValue);
        }

        this.onTouched();
    }

    protected handleBlur(): void {
        this.onTouched();
    }

    private toMdy(date: Date): string {
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        const y = date.getFullYear();
        return `${m}/${d}/${y}`;
    }
}
